'use client'

import { ApolloLink, CombinedGraphQLErrors, HttpLink } from '@apollo/client'
import { useRouter } from '@/navigation'
import { ErrorLink } from '@apollo/client/link/error'
import { SetContextLink } from '@apollo/client/link/context'
import {
  ApolloClient,
  ApolloNextAppProvider,
  InMemoryCache,
  SSRMultipartLink
} from '@apollo/client-integration-nextjs'
import Bugsnag from '@bugsnag/js'
import { Country, useCountry } from '@/app/country'
import { defaultLocale, locales } from '@/i18n/request'
import { Locale } from '@/types/locale'
import { useSessionStore } from '@/app/store/session'

const resolveLocale = (): Locale => {
  if (typeof window === 'undefined') return defaultLocale

  const segment = window.location.pathname.split('/').filter(Boolean)[0]
  const normalized = segment?.toLowerCase()

  return normalized && locales.includes(normalized as Locale)
    ? (normalized as Locale)
    : defaultLocale
}

// Token of the Contact of the tab, sent with each GraphQL request
const resolveSecurityToken = () =>
  useSessionStore.getState().contact.securityToken

const sessionLink = new SetContextLink(({ headers }) => {
  const locale = resolveLocale()
  const securityToken = resolveSecurityToken()

  return {
    headers: {
      ...headers,
      'X-Locale': locale,
      ...(securityToken ? { 'X-Security-Token': securityToken } : {})
    }
  }
})

const whitelistedErrorOperations = [
  'applyBillingCycle',
  'applyPromotion',
  'authenticate',
  'createUser',
  'formalitiesPage'
]
// @ts-expect-error
const errorLink = (router) =>
  new ErrorLink(({ error, operation }) => {
    // Operation whose caller shows the error itself (billing step): no error page
    if (operation.getContext().skipErrorRedirect) return
    if (CombinedGraphQLErrors.is(error)) {
      if (whitelistedErrorOperations.includes(operation.operationName ?? ''))
        return
      router.push(`/error?message=${error.errors[0].message}`)
      error.errors.forEach(({ message }) => Bugsnag.notify(message))
      return
    }
    // Network error, or a response that is not a GraphQL result
    Bugsnag.notify(error)
    router.push(`/error?message=${encodeURIComponent(error.message)}`)
  })

const host = (country: Country) => {
  if (country === 'FR') {
    return process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT_FR
  } else if (country === 'BE') {
    return process.env.NEXT_PUBLIC_GRAPHQL_ENDPOINT_BE
  }
}

// @ts-expect-error
const makeClient = (router, country) => () => {
  const httpLink = new HttpLink({
    uri: host(country),
    credentials: 'include',
    fetchOptions: { cache: 'no-store' }
  })

  return new ApolloClient({
    // InMemoryCache of @apollo/client-integration-nextjs, which streams the server results to the browser
    cache: new InMemoryCache(),
    // Apollo Client 4 sets loading back to true while a query is refetched (refetchQueries of the address and
    // promo code steps); kept to false as in Apollo Client 3, so the pages do not show their spinner again
    defaultOptions: { watchQuery: { notifyOnNetworkStatusChange: false } },
    link:
      typeof window === 'undefined'
        ? ApolloLink.from([
            errorLink(router),
            // in a SSR environment, if you use multipart features like
            // @defer, you need to decide how to handle these.
            // This strips all interfaces with a `@defer` directive from your queries.
            new SSRMultipartLink({
              stripDefer: true
            }),
            sessionLink.concat(httpLink)
          ])
        : ApolloLink.from([errorLink(router), sessionLink.concat(httpLink)])
  })
}

export function ApolloWrapper({ children }: React.PropsWithChildren) {
  const router = useRouter()
  const country = useCountry()

  return (
    <ApolloNextAppProvider makeClient={makeClient(router, country)}>
      {children}
    </ApolloNextAppProvider>
  )
}
